import { useMemo, useCallback } from 'react';
import {
  SelectedFieldModel,
  FieldStateModel,
  DEFAULT_FIELD_STATE,
  FieldModel,
} from 'models/Enterprise/FieldsManagerModel';

const CalculationType = {
  Mean: 1,
  Percentage: 2,
  Nps: 3,
  TotalCount: 4,
};

const TYPES_WITH_STAT_TESTING: number[] = [
  CalculationType.Mean,
  CalculationType.Percentage,
  CalculationType.Nps,
];

const TYPES_WITH_ADVANCED_SETTINGS: number[] = [
  CalculationType.Percentage,
  CalculationType.Nps,
  CalculationType.TotalCount,
];

/**
 * Field state for the pinned "Project Frequency" field.
 * Only the Display Name is visible/editable; every other control is hidden.
 */
const PROJECT_FREQUENCY_FIELD_STATE: FieldStateModel = {
  SelectField: { visible: false, disabled: true },
  DisplayName: { visible: true, disabled: false },
  CalculationType: { visible: false, disabled: true },
  Precision: { visible: false, disabled: true },
  Normalize: { visible: false, disabled: true },
  StatTesting: { visible: false, disabled: true },
  isL1Category: { visible: false, disabled: true },
  AdvancedSettings: { visible: false, disabled: true },
  Filters: { visible: false, disabled: true },
};

/**
 * Checks if calculation type should show Statistical Testing checkbox
 * StatTesting is shown for: Mean, Percentage, NPS
 */
const shouldShowStatTesting = (calculationType: number | null | undefined): boolean => {
  if (!calculationType) return false;

  return TYPES_WITH_STAT_TESTING.includes(calculationType);
};

/**
 * Checks if calculation type should show the Advanced Settings link.
 * Advanced Settings is shown for: Percentage, NPS
 */
const shouldShowAdvancedSettings = (calculationType: number | null | undefined): boolean => {
  if (!calculationType) return false;
  return TYPES_WITH_ADVANCED_SETTINGS.includes(calculationType);
};

/**
 * Checks if calculation type should show the Highlight checkbox.
 * Highlight is shown ONLY for Percentage (2).
 * Hidden for all other types: Mean/Frequency (1) forced to true, NPS (3) forced to false, Total Count (4) forced to true.
 */
const shouldShowHighlight = (
  calculationType: number | null | undefined,
  hasCalculationType: boolean
): boolean => {
  if (!hasCalculationType || !calculationType) return false;
  // Show only for Percentage (2)
  return calculationType === CalculationType.TotalCount;
};

/**
 * Checks if Decimal Places (Precision) should be visible.
 * Precision is hidden for TotalCount (type 4 / Totals).
 */
const shouldShowPrecision = (calculationType: number | null | undefined): boolean => {
  return calculationType !== CalculationType.TotalCount;
};

/**
 * Checks if field has responses with Flag values that require normalization
 * Normalize is shown when field Responses contain Flag: "DK", "NA", or "REF"
 */
export const shouldShowNormalize = (field: FieldModel): boolean => {
  if (!field.responses || !Array.isArray(field.responses)) {
    return false;
  }

  return field.responses.some((response: any) => {
    return response.flag === 'DK' || response.flag === 'NA' || response.flag === 'REF';
  });
};

/**
 * Custom hook to memoize field state computation function.
 * Returns a stable reference to prevent unnecessary recalculations.
 *
 * @returns Memoized computeFieldState function
 */
const useComputeFieldState = () => {
  return useCallback(
    (field: SelectedFieldModel, useDiyMetricLevelFilters?: boolean): FieldStateModel => {
      if (field.isProjectFrequency) {
        return PROJECT_FREQUENCY_FIELD_STATE;
      }

      const hasCalculationType = field.calculationType !== null;
      const hasFieldSelected = field.field.fieldGuid !== '';

      const calculationType = field.calculationType?.type
        ? Number(field.calculationType.type)
        : null;
      const showStatTesting = hasCalculationType && shouldShowStatTesting(calculationType);
      const showNormalize = hasFieldSelected && shouldShowNormalize(field.field);
      const showHighlight = shouldShowHighlight(calculationType, hasCalculationType);
      const showAdvancedSettings =
        hasCalculationType && shouldShowAdvancedSettings(calculationType);
      const showPrecision = shouldShowPrecision(calculationType);
      const showFilters = useDiyMetricLevelFilters ?? false;

      return {
        ...DEFAULT_FIELD_STATE,
        CalculationType: {
          visible: true,
          disabled: !hasFieldSelected,
        },
        Precision: {
          visible: showPrecision,
          disabled: !showPrecision,
        },
        Normalize: {
          visible: showNormalize,
          disabled: !showNormalize,
        },
        StatTesting: {
          visible: showStatTesting,
          disabled: !showStatTesting,
        },
        isL1Category: {
          visible: showHighlight,
          disabled: !showHighlight,
        },
        AdvancedSettings: {
          visible: showAdvancedSettings,
          disabled: !showAdvancedSettings,
        },
        Filters: {
          visible: showFilters,
          disabled: !showFilters,
        },
      };
    },
    []
  );
};

/**
 * Custom hook to compute field states for all fields in the collection.
 * Memoized to prevent unnecessary recalculations.
 *
 * Business Rules:
 * - Normalize and StatTesting are hidden by default
 * - StatTesting: Visible and enabled when calculation type is Mean, Percentage, or NPS
 * - StatTesting: Hidden for TotalCount and other calculation types
 * - Precision (Decimal Places): Hidden for TotalCount
 * - Normalize: Visible when field has options with Flag "DK", "NA", or "REF" (defaulted to false)
 * - Normalize: Hidden for all other cases
 * - CalculationType: Enabled only when a field is selected
 *
 * @param fields - Array of selected field models
 * @returns Map of field index to computed FieldStateModel
 */
export const useFieldStates = (
  fields: SelectedFieldModel[],
  useDiyMetricLevelFilters?: boolean
): Map<number, FieldStateModel> => {
  const computeFieldState = useComputeFieldState();

  return useMemo(() => {
    const stateMap = new Map<number, FieldStateModel>();
    fields.forEach((field, index) => {
      stateMap.set(index, computeFieldState(field, useDiyMetricLevelFilters));
    });
    return stateMap;
  }, [fields, computeFieldState]);
};
